import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { NotFoundException } from "@zxing/library";
import { verifyExposureToken } from "../api/nft";
import { useTranslation } from "react-i18next";
import { maskNumistrailId } from "../utils/mask";

const statusTone = (status, isMarketplace) => {
  if (status === "valid") return isMarketplace ? "warning" : "positive";
  if (status === "not_trusted") return "warning";
  if (status === "scheduled") return "info";
  return "negative";
};

export function Verify() {
  const { t } = useTranslation();
  const [manualPayload, setManualPayload] = useState("");
  const [result, setResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [scanError, setScanError] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [allowManual, setAllowManual] = useState(false);
  const [manualVisible, setManualVisible] = useState(false);
  const [checklist, setChecklist] = useState({ cert: false, platform: false, username: false });
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const frameRef = useRef(null);
  const zxingReaderRef = useRef(null);
  const zxingControlsRef = useRef(null);

  const stopCamera = useCallback(() => {
    if (zxingControlsRef.current) {
      if (typeof zxingControlsRef.current.stop === "function") {
        zxingControlsRef.current.stop();
      }
      zxingControlsRef.current = null;
    }
    if (zxingReaderRef.current) {
      if (typeof zxingReaderRef.current.reset === "function") {
        zxingReaderRef.current.reset();
      }
      zxingReaderRef.current = null;
    }
    if (frameRef.current) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  const stopScanner = useCallback(() => {
    setIsScanning(false);
    stopCamera();
  }, [stopCamera]);

  const submitPayload = useCallback(async (payload) => {
    const trimmed = (payload || "").trim();
    if (!trimmed) {
      setScanError(t("verify.errors.payloadMissing"));
      return;
    }
    setIsLoading(true);
    setScanError(null);
    setResult(null);
    try {
      const { data } = await verifyExposureToken(trimmed);
      setResult(data);
    } catch (err) {
      const key = err.response?.data?.message_key;
      const message = err.response?.data?.message;
      setScanError(
        t(key || "errors.verifyFailed", {
          defaultValue: message || t("errors.verifyFailed"),
        })
      );
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  const handleScanSuccess = useCallback((payload) => {
    stopScanner();
    submitPayload(payload);
  }, [stopScanner, submitPayload]);

  useEffect(() => {
    if (!isScanning) {
      stopCamera();
      return () => {};
    }
    let cancelled = false;

    if (!navigator.mediaDevices?.getUserMedia) {
      setScanError(t("verify.errors.noLiveScan"));
      setIsScanning(false);
      setAllowManual(true);
      setManualVisible(false);
      return () => {};
    }

    const startNativeScan = async () => {
      try {
        const detector = new window.BarcodeDetector({ formats: ["qr_code"] });
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
        const scanFrame = async () => {
          if (cancelled || !videoRef.current) return;
          try {
            const barcodes = await detector.detect(videoRef.current);
            if (barcodes.length && barcodes[0].rawValue) {
              handleScanSuccess(barcodes[0].rawValue);
              return;
            }
          } catch (err) {
            setScanError(t("verify.errors.cameraUnavailable"));
          }
          frameRef.current = requestAnimationFrame(scanFrame);
        };
        scanFrame();
      } catch (err) {
        setScanError(t("verify.errors.cameraAccess"));
        setAllowManual(true);
        setManualVisible(false);
        setIsScanning(false);
      }
    };

    const startZxingScan = async () => {
      try {
        const reader = new BrowserMultiFormatReader();
        zxingReaderRef.current = reader;
        const controls = await reader.decodeFromConstraints(
          { video: { facingMode: "environment" } },
          videoRef.current,
          (result, err) => {
            if (cancelled) return;
            if (result?.getText) {
              handleScanSuccess(result.getText());
            } else if (err && !(err instanceof NotFoundException)) {
              setScanError(t("verify.errors.cameraUnavailable"));
            }
          }
        );
        if (cancelled) {
          controls.stop();
          return;
        }
        zxingControlsRef.current = controls;
        if (videoRef.current && videoRef.current.srcObject instanceof MediaStream) {
          streamRef.current = videoRef.current.srcObject;
        }
      } catch (err) {
        if (!cancelled) {
          setScanError(t("verify.errors.cameraAccess"));
          setAllowManual(true);
          setManualVisible(false);
          setIsScanning(false);
        }
      }
    };

    if ("BarcodeDetector" in window) {
      startNativeScan();
    } else {
      startZxingScan();
    }
    return () => {
      cancelled = true;
      stopCamera();
    };
  }, [handleScanSuccess, isScanning, stopCamera]);

  const attentionText = useMemo(() => {
    if (!result) return null;
    if (result.attention_text_key) {
      return t(result.attention_text_key, { defaultValue: result.attention_text });
    }
    if (result.attention_text) return result.attention_text;
    return null;
  }, [result, t]);

  useEffect(() => {
    if (!result) {
      setChecklist({ cert: false, platform: false, username: false });
      return;
    }
    const isMarketplace = Boolean(result.platform_name) && result.status === "valid";
    if (isMarketplace) {
      setChecklist({ cert: false, platform: false, username: false });
    } else {
      setChecklist({ cert: false, platform: false, username: false });
    }
  }, [result]);

  const toggleChecklist = (field) => (event) => {
    const checked = event.target.checked;
    setChecklist((prev) => ({ ...prev, [field]: checked }));
  };

  const renderResult = () => {
    if (!result) return null;
    const isMarketplace = Boolean(result.platform_name);
    const allChecked = isMarketplace && checklist.cert && checklist.platform && checklist.username;
    const tone = isMarketplace
      ? (allChecked ? "positive" : "warning")
      : statusTone(result.status, isMarketplace);
    const statusMessage = (() => {
      if (result.status === "valid") {
        if (isMarketplace) {
          return allChecked ? t("verify.status.valid.confirmed") : t("verify.status.valid.review");
        }
        return t("verify.status.valid.inPerson");
      }
      if (result.status === "not_trusted") return t("verify.status.notTrusted");
      if (result.status === "scheduled") return t("verify.status.scheduled");
      return t("verify.status.invalid");
    })();
    const isValid = result.status === "valid";
    const isNotTrusted = result.status === "not_trusted";
    const isScheduled = result.status === "scheduled";
    const isRemoteListing = isValid && isMarketplace;
    const notTrustedMessage = result.message_key
      ? t(result.message_key, { defaultValue: result.message || t("verify.messages.notTrusted") })
      : (result.message || t("verify.messages.notTrusted"));
    const metaItems = [];
    if (result.certification_authority || result.certification_number) {
      metaItems.push({
        label: t("verify.meta.certification"),
        value: `${result.certification_authority || ""} ${result.certification_number || ""}`.trim(),
      });
    }
    if (isMarketplace && result.platform_name) {
      metaItems.push({ label: t("verify.meta.marketplace"), value: result.platform_name });
    }
    if (isMarketplace && result.platform_username) {
      const platformLabel = result.platform_name || t("verify.meta.platform");
      metaItems.push({ label: t("verify.meta.platformUsername", { platform: platformLabel }), value: result.platform_username });
    }
    if (!isMarketplace && result.owner_numistrail_id) {
      metaItems.push({ label: t("verify.meta.owner"), value: maskNumistrailId(result.owner_numistrail_id) });
    }
    const showCode = isValid && !isMarketplace && result.confirmation_code;

    return (
      <div className={`verify-result verify-result--${tone}`}>
        <p className="verify-result__status">{statusMessage}</p>
        {isRemoteListing && (
          <p className="verify-result__message">
            {t("verify.messages.remoteChecklist")}
          </p>
        )}
        {isNotTrusted && (
          <p className="verify-result__message">{notTrustedMessage}</p>
        )}
        {isValid && !isMarketplace && (
          <p className="verify-result__message">{t("verify.messages.verifyCode")}</p>
        )}
        {!isValid && !isScheduled && !isNotTrusted && (
          <p className="verify-result__message">{t("verify.messages.newQr")}</p>
        )}
        {isScheduled && (
          <p className="verify-result__message">{t("verify.messages.shareCurrentQr")}</p>
        )}
        {(isValid || isNotTrusted) && metaItems.length > 0 && (
          <ul className="verify-result__meta">
            {metaItems.map((item) => (
              <li key={item.label}>
                <span>{item.label}</span>
                <strong>{item.value || t("common.notAvailable")}</strong>
              </li>
            ))}
            {showCode && (
              <li className="verify-result__code">
                <span>{t("verify.meta.code")}</span>
                <strong>{result.confirmation_code}</strong>
              </li>
            )}
          </ul>
        )}
        {isRemoteListing && (
          <div className="verify-checklist">
            <label>
              <input type="checkbox" checked={checklist.cert} onChange={toggleChecklist("cert")} />
              {t("verify.checklist.cert")}
            </label>
            <label>
              <input type="checkbox" checked={checklist.platform} onChange={toggleChecklist("platform")} />
              {t("verify.checklist.marketplace")}
            </label>
            <label>
              <input type="checkbox" checked={checklist.username} onChange={toggleChecklist("username")} />
              {t("verify.checklist.username", { platform: result.platform_name || t("verify.meta.marketplace") })}
            </label>
          </div>
        )}
        {attentionText && <p className="verify-result__notice">⚠ {attentionText}</p>}
      </div>
    );
  };

  return (
    <div className="verify-screen">
      <div className="verify-card">
        <h1>{t("verify.title")}</h1>
        <p className="verify-text">{t("verify.subtitle")}</p>
        <div className="verify-actions">
          <button
            type="button"
            className={`verify-scan-button${isScanning ? " verify-scan-button--active" : ""}`}
            onClick={() => { setResult(null); setScanError(null); setIsScanning(true); }}
          >
            {isScanning ? t("verify.scanning") : t("verify.scanCta")}
          </button>
          {allowManual && !manualVisible && (
            <button type="button" className="verify-scan-button" onClick={() => setManualVisible(true)}>
              {t("verify.manualCta")}
            </button>
          )}
          {manualVisible && (
            <div className="verify-manual">
              <input
                type="text"
                placeholder={t("verify.payloadPlaceholder")}
                value={manualPayload}
                onChange={(event) => setManualPayload(event.target.value)}
              />
              <button
                type="button"
                className="verify-scan-button verify-scan-button--active"
                onClick={() => submitPayload(manualPayload)}
                disabled={isLoading}
              >
                {isLoading ? t("verify.verifying") : t("verify.verifyCodeCta")}
              </button>
            </div>
          )}
        </div>
        {scanError && <p className="verify-error">{scanError}</p>}
        {renderResult()}
      </div>
      {isScanning && (
        <div className="scanner-overlay" role="dialog" aria-modal="true">
          <div className="scanner-shell">
            <video ref={videoRef} autoPlay playsInline muted className="scanner-video" />
            <button
              type="button"
              className="verify-scan-button verify-scan-button--active scanner-stop"
              onClick={stopScanner}
            >
              {t("verify.stopScanning")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
